import { useMemo } from "react"
import { BoneMesh } from "./BoneMesh.tsx"
import { noHit } from "./Entity.ts"
import { useBufferObject } from "./hooks/useBufferObject.ts"
import { useDrawCall } from "./hooks/useDrawCall.tsx"
import { useGL } from './hooks/useGL.ts'
import { useLighting } from "./hooks/useLighting.ts"
import { useProgram } from "./hooks/useProgram.ts"
import { useResource } from "./hooks/useResource.ts"
import { PosedSkeleton } from "./PosedSkeleton.ts"
import fragmentSource from './SkeletonView.fs?raw'
import vertexSource from './SkeletonView.vs?raw'

export function SkeletonView(props: {
  skeleton: PosedSkeleton
  enabled: boolean
  selectedBone: number | null
}) {
  const { skeleton, enabled, selectedBone } = props

  const gl = useGL()
  const program = useProgram('Skeleton', gl, vertexSource, fragmentSource)
  const locations = useMemo(() => ({
    a_pBoneMesh: gl.getAttribLocation(program, 'a_pBoneMesh'),
    a_nBoneMesh: gl.getAttribLocation(program, 'a_nBoneMesh'),
    a_worldFromBoneMesh: gl.getAttribLocation(program, 'a_worldFromBoneMesh'),
    a_worldFromBoneMesh_normal: gl.getAttribLocation(program, 'a_worldFromBoneMesh_normal'),

    u_clipFromWorld: gl.getUniformLocation(program, 'u_clipFromWorld'),
    u_hoveredInstance: gl.getUniformLocation(program, 'u_hoveredInstance'),
    u_selectedInstance: gl.getUniformLocation(program, 'u_selectedInstance'),
  }), [gl, program])

  const vbo_pBoneMesh = useBufferObject('Bone positions', gl, gl.ARRAY_BUFFER, gl.STATIC_DRAW)
  const vbo_nBoneMesh = useBufferObject('Bone normals', gl, gl.ARRAY_BUFFER, gl.STATIC_DRAW)
  const vbo_worldFromBoneMesh = useBufferObject('worldFromBone', gl, gl.ARRAY_BUFFER, gl.STATIC_DRAW)
  const vbo_worldFromBoneMesh_normal = useBufferObject('worldFromBone_normal', gl, gl.ARRAY_BUFFER, gl.STATIC_DRAW)

  const vao = useResource(function* () {
    const vao = gl.createVertexArray()
    gl.bindVertexArray(vao)

    gl.enableVertexAttribArray(locations.a_pBoneMesh)
    vbo_pBoneMesh.bind(() => {
      gl.vertexAttribPointer(locations.a_pBoneMesh, 3, gl.FLOAT, false, 0, 0)
    })

    gl.enableVertexAttribArray(locations.a_nBoneMesh)
    vbo_nBoneMesh.bind(() => {
      gl.vertexAttribPointer(locations.a_nBoneMesh, 3, gl.FLOAT, false, 0, 0)
    })

    vbo_worldFromBoneMesh.bind(() => {
      for (let i = 0; i < 4; ++i) {
        gl.enableVertexAttribArray(locations.a_worldFromBoneMesh + i)
        gl.vertexAttribDivisor(locations.a_worldFromBoneMesh + i, 1)
        gl.vertexAttribPointer(
          locations.a_worldFromBoneMesh + i,
          4, gl.FLOAT, false,
          4 * 4 * 4, 4 * 4 * i
        )
      }
    })

    vbo_worldFromBoneMesh_normal.bind(() => {
      for (let i = 0; i < 3; ++i) {
        gl.enableVertexAttribArray(locations.a_worldFromBoneMesh_normal + i)
        gl.vertexAttribDivisor(locations.a_worldFromBoneMesh_normal + i, 1)
        gl.vertexAttribPointer(
          locations.a_worldFromBoneMesh_normal + i,
          3, gl.FLOAT, false,
          3 * 3 * 4, 3 * 4 * i
        )
      }
    })

    gl.bindVertexArray(null)

    yield vao

    gl.deleteVertexArray(vao)
  }, [gl, locations, vbo_pBoneMesh, vbo_nBoneMesh, vbo_worldFromBoneMesh, vbo_worldFromBoneMesh_normal])

  const instanceCount = (skeleton.skeleton.bones).length

  const runLighting = useLighting(gl, program)

  const transformsData = useMemo(() => {
    const transforms = skeleton.worldFromBoneMesh_posedTransforms()
    return {
      worldFromBone: Float32Array.from(transforms
        .values()
        .flatMap(mat => mat)),
      worldFromBone_normal: Float32Array.from(transforms
        .map(mat => mat.basis.normalTransform)
        .values()
        .flatMap(mat => mat)),
    }
  }, [skeleton])

  useDrawCall('SkeletonView', ({ gl, clipFromWorld, mouseHit }) => {
    if (!enabled || !gl.isProgram(program) || !gl.isVertexArray(vao) || instanceCount === 0) {
      return
    }

    if (mouseHit.who !== skeleton) {
      mouseHit = noHit
    }

    vbo_pBoneMesh.fill(BoneMesh.positions)
    vbo_nBoneMesh.fill(BoneMesh.normals)
    vbo_worldFromBoneMesh.fill(transformsData.worldFromBone)
    vbo_worldFromBoneMesh_normal.fill(transformsData.worldFromBone_normal)

    gl.useProgram(program)
    gl.bindVertexArray(vao)
    gl.uniformMatrix4fv(locations.u_clipFromWorld, false, Float32Array.from(clipFromWorld))
    gl.uniform1i(locations.u_hoveredInstance, mouseHit == noHit ? -1 : mouseHit.extra as number)
    gl.uniform1i(locations.u_selectedInstance, selectedBone ?? -1)

    runLighting(() => {
      gl.drawArraysInstanced(gl.TRIANGLES, 0, BoneMesh.positions.length / 3, instanceCount)
    })

    gl.bindVertexArray(null)
    gl.useProgram(null)
  }, [gl, program, locations, vao, runLighting, instanceCount, enabled, selectedBone, transformsData])

  return null
}
